import { Switch, Route, useRoute, Redirect } from "wouter";
import { lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { TenantUnitProvider } from "@/contexts/TenantUnitContext";
import { TenantUnitGuard } from "@/components/guards/TenantUnitGuard";
import { OwnerUnitGuard } from "@/components/guards/OwnerUnitGuard";
import { DisclaimerGuard } from "@/components/guards/DisclaimerGuard";
import { ElectronicRecordsConsentGuard } from "@/components/guards/ElectronicRecordsConsentGuard";
import { OwnerUnitProvider } from "@/features/owner-portal/context/OwnerUnitContext";
import { CompanyProvider } from "@/providers/CompanyProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequirePM } from "@/components/auth/RequirePM";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { RequireSuperAdmin } from "@/components/auth/RequireSuperAdmin";
import { RequireBoardMember } from "@/components/auth/RequireBoardMember";
import { IntlProvider } from "./i18n";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { RateLimitNotice } from "@/components/notifications";
import { AuthLoadError } from "@/components/auth/AuthLoadError";
import { PageSkeleton } from "@/components/PageSkeleton";
import { useReturnPath } from "@/lib/returnPath";

// ── Eager-loaded pages (always in initial bundle) ──
import NotFound from "@/pages/not-found";
import SandboxErrorTrigger from "@/pages/sandbox/ErrorTrigger";
import SentryPing from "@/pages/sandbox/SentryPing";
import Landing from "@/pages/Landing";

// ── Lazy-loaded pages (split into separate chunks) ──
// Auth pages — lazy to keep zod + react-hook-form out of First Load JS
// EP-10 / IA-05 (868k9688w) — resident assistant. Lazy so the bundle a PM or
// a board member downloads does not carry a panel they never see.
const OwnerIaAssistant = lazy(() =>
  import("@/features/ia").then((m) => ({ default: m.OwnerIaAssistant })),
);
const TenantIaAssistant = lazy(() =>
  import("@/features/ia").then((m) => ({ default: m.TenantIaAssistant })),
);

const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmailChange = lazy(() => import("@/pages/VerifyEmailChange"));
const EmailPreferences = lazy(() => import("@/pages/EmailPreferences"));
// Account y Settings — poco frecuentes, no justifican First Load JS
const Account = lazy(() => import("@/pages/Account"));
// SA-16 — Settings hub for SA + PM, per-user preferences for everyone
// else. The branch resolves the right surface based on the actor's role.
const SettingsRouteSwitch = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.SettingsRouteSwitch })),
);
const DisclaimerPage = lazy(() => import("@/features/legal"));
const ConsentPage = lazy(() => import("@/features/consent/ConsentPage"));
// Dashboard pages — lazy to keep recharts out of First Load JS
// PM-03 + SA-15 (EP-07 ADR 011): the SA uses the SAME dashboard page as
// the PM, with intra-org scope (CA-03 guardrail). The SA's organization
// resolves to the actor's home org; only `getSaKpis` is the
// cross-org surface (platform snapshot, BYPASSRLS + audit row).
const Dashboard = lazy(() => import("@/features/pm-dashboard").then((m) => ({ default: m.PmDashboardPage })));
const PmDashboard = lazy(() => import("@/features/pm-dashboard").then((m) => ({ default: m.PmDashboardPage })));
const MyUnit = lazy(() => import("@/portals/tenant/pages/my-unit"));
const TenantAnnouncementsPage = lazy(() =>
  import("@/portals/tenant/pages/announcements/TenantAnnouncementsPage")
);
const TenantFinancial = lazy(() =>
  import("@/portals/tenant/pages/financial/TenantFinancialPage")
);
const TenantUnitSelectPage = lazy(() =>
  import("@/portals/tenant/pages/unit-select/TenantUnitSelectPage")
);
const OwnerUnits = lazy(() => import("@/pages/owner/OwnerUnits"));
const OwnerUnitDetail = lazy(() => import("@/pages/owner/OwnerUnitDetail"));
const OwnerOverview = lazy(() => import("@/pages/owner/OwnerOverviewPage"));
const OwnerFinancial = lazy(() => import("@/pages/owner/OwnerFinancialPage"));
const OwnerUnitSelectPage = lazy(() => import("@/pages/owner/OwnerUnitSelectPage"));
const OwnerAnnouncements = lazy(() => import("@/pages/owner/OwnerAnnouncements"));
const OwnerWorkOrders = lazy(() => import("@/pages/owner/OwnerWorkOrders"));
const OwnerWorkOrderCreate = lazy(() => import("@/pages/owner/OwnerWorkOrderCreate"));
const OwnerBookings = lazy(() => import("@/pages/owner/OwnerBookings"));
// [868kbhrky] `OwnerBookingCreate` lazy import REMOVED — the form is
// gone. The `/owner/bookings/new` route now mounts OwnerBookings with
// `initialView="reservar"` (a prop that forces the booking-section view
// instead of the default list view). See OwnerBookings.tsx for the
// initialView prop contract.
const OwnerDocuments = lazy(() => import("@/pages/owner/OwnerDocuments"));
const OwnerMotion = lazy(() => import("@/pages/owner/OwnerMotion"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertySettings = lazy(() => import("@/features/property-settings"));
const Units = lazy(() => import("@/pages/Units"));
const Owners = lazy(() => import("@/features/owners/OwnersPage"));
const Announcements = lazy(() => import("@/features/announcements/AnnouncementsPage"));
const Renters = lazy(() => import("@/pages/Renters"));
const WorkOrders = lazy(() => import("@/features/work-orders"));
const PaymentsModule = lazy(() => import("@/pages/payments-module"));
const RenterWorkOrdersView = lazy(() =>
  import("@/portals/tenant/pages/work-orders/RenterWorkOrdersView").then((m) => ({
    default: m.RenterWorkOrdersView,
  }))
);
const RenterWorkOrderCreatePage = lazy(() =>
  import("@/portals/tenant/pages/work-orders/RenterWorkOrderCreatePage").then((m) => ({
    default: m.RenterWorkOrderCreatePage,
  }))
);
const RenterDocumentsView = lazy(() =>
  import("@/portals/tenant/pages/documents/RenterDocumentsView").then((m) => ({
    default: m.RenterDocumentsView,
  }))
);
const AccountStatement = lazy(() => import("@/pages/payments-module/AccountStatementPage"));
// FIN-19 — Reports section. Lazy-loads `features/reports` so the bundle
// does not pay for the form/Cards unless the user reaches the route. The
// permission gate mirrors `navigation.config.ts` (`reports: read`) so the
// sidebar entry and the route-guard cannot diverge.
const ReportsSectionPage = lazy(() =>
  import("@/features/reports").then((m) => ({ default: m.ReportsSectionPage }))
);
const Documents = lazy(() => import("@/pages/Documents"));
// OR-02 (868kmgfck) CA-08: the board member's read-only screen for the
// statutory records of the association. It renders the SAME section the
// owner portal renders.
const OfficialRecordsBoardPage = lazy(() =>
  import("@/features/official-records").then((m) => ({ default: m.OfficialRecordsBoardPage }))
);
const IaGaps = lazy(() => import("@/features/ia-gaps"));
const IaKbIngest = lazy(() => import("@/features/ia-kb-ingest"));
const AccessControl = lazy(() => import("@/pages/AccessControl"));
const Violations = lazy(() => import("@/features/violations"));
// VOTE-06 — motions list and history (EP-09): the web list, and the owner's.
const Votes = lazy(() => import("@/features/votes"));
const OwnerVotes = lazy(() =>
  import("@/features/votes").then((m) => ({ default: m.OwnerVotesPage }))
);
const CreateMotion = lazy(() =>
  import("@/features/votes").then((m) => ({ default: m.CreateMotionPage }))
);
const MotionDetail = lazy(() =>
  import("@/features/votes").then((m) => ({ default: m.MotionDetailPage }))
);
const Vendors = lazy(() => import("@/pages/Vendors"));
const Bookings = lazy(
  () => import("@/features/bookings").then((m) => ({ default: m.BookingsPage }))
);
const Budgets = lazy(() => import("@/pages/Budgets"));
const BudgetDetail = lazy(() => import("@/pages/BudgetDetail"));
const PaymentSettings = lazy(() => import("@/pages/admin/PaymentSettings"));
const JobSchedulesTrigger = lazy(() => import("@/pages/admin/JobSchedulesTrigger"));
const ConfigurationManager = lazy(() => import("@/pages/admin/ConfigurationManager"));
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const LegalDisclaimerAdmin = lazy(() => import("@/pages/admin/LegalDisclaimerAdmin"));
const JobFailuresPage = lazy(() => import("@/pages/admin/JobFailures"));
const BudgetCategories = lazy(() => import("@/features/budget-categories"));
const LookupManagement = lazy(() => import("@/pages/admin/LookupManagement"));
const LookupValueManagement = lazy(() => import("@/pages/admin/LookupValueManagement"));
const AmenitiesConfig = lazy(() => import("@/pages/AmenitiesConfig"));
const COAConfiguration = lazy(() => import("@/pages/COAConfiguration"));
const COAConfigurationEdit = lazy(() => import("@/pages/COAConfigurationEdit"));
const Organizations = lazy(() => import("@/pages/admin/Organizations"));
const OrganizationDetail = lazy(() => import("@/pages/admin/organizations/OrganizationDetail"));
const Companies = lazy(() => import("@/pages/Companies"));
const CompanyDetail = lazy(() => import("@/pages/CompanyDetail"));

function Router() {
  const { user, isAuthenticated, isLoading, authError, refetch } = useAuth();
  const [isSandbox] = useRoute('/sandbox/error-trigger');
  const [isSentryPing] = useRoute('/sandbox/sentry-ping');
  const [isEmailPreferences] = useRoute('/email-preferences');
  // VOTE-02 CA-02: a vote's email opened without a session lands on Landing;
  // its path is kept and followed once, on signing in (Google included).
  // 868mbzt38 D5 — `authError !== null` keeps the return path OFF: a 4xx/5xx
  // doesn't strip the URL out of sessionStorage the way `!isAuthenticated`
  // would.
  useReturnPath(isLoading || authError !== null, isAuthenticated);

  // Sandbox route is always available regardless of auth/role (QA only)
  if (isSandbox) {
    return <SandboxErrorTrigger />;
  }

  if (isSentryPing) {
    return <SentryPing />;
  }

  // [868m31u7a] The footer link of every email opens this page with or
  // without a session: the signed token in the URL is the credential.
  if (isEmailPreferences) {
    return <EmailPreferences />;
  }

  // Show loading skeleton while auth resolves — Landing would render
  // without sidebar/PM elements, causing E2E waitForPMAuth() timeouts.
  if (isLoading) {
    return <PageSkeleton />;
  }

  // 868mbzt38 D5 — non-401 auth error with no cached user. Render a full-
  // page notice instead of Landing/Login: the user IS signed in (their
  // session cookie is still valid locally), the query just failed. They
  // should retry rather than re-login. The Retry button calls
  // `useAuth.refetch` directly.
  if (authError !== null) {
    return <AuthLoadError error={authError} onRetry={refetch} />;
  }

  // Show different pages for unauthenticated users
  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/verify-email-change" component={VerifyEmailChange} />
        <Route component={Landing} />
      </Switch>
    );
  }

  // Role-based routing for authenticated users
  if (user?.role === "renter") {
    return (
      <TenantUnitProvider>
        <TenantUnitGuard>
        {/* ESIGN-01 CA-01: the electronic records consent is asked once, on the first visit after activation. */}
        <ElectronicRecordsConsentGuard>
        <Switch>
          <Route path="/unit-select" component={TenantUnitSelectPage} />
          {/* CONSENT-01 CA-09: la pagina deriva la superficie del rol, no de la URL. */}
          <Route path="/consent/:consentType" component={ConsentPage} />
          <Route path="/" component={MyUnit} />
          <Route path="/dashboard"><Redirect to="/my-unit" /></Route>
          <Route path="/my-unit" component={MyUnit} />
          <Route path="/announcements" component={TenantAnnouncementsPage} />
          {/* REN-SUG-07: renter-side finance consolidated into /tenant/financial */}
          <Route path="/tenant/financial" component={TenantFinancial} />
          <Route path="/tenant/work-orders/new" component={RenterWorkOrderCreatePage} />
          <Route path="/work-orders/new" component={RenterWorkOrderCreatePage} />
          <Route path="/tenant/work-orders" component={RenterWorkOrdersView} />
          <Route path="/work-orders" component={RenterWorkOrdersView} />
          <Route path="/documents" component={RenterDocumentsView} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/verify-email-change" component={VerifyEmailChange} />
          <Route path="/bookings" component={Bookings} />
          <Route path="/account" component={Account} />
          <Route path="/settings" component={SettingsRouteSwitch} />
          <Route component={NotFound} />
        </Switch>
        </ElectronicRecordsConsentGuard>
        {/* Mounted inside the guard so the assistant only exists once the
            person has a unit, and inside this branch so PM, board and SA are
            excluded structurally rather than by a condition someone can
            later loosen. Its own Suspense with a null fallback: the panel
            must never delay the page it sits on. */}
        <Suspense fallback={null}>
          <TenantIaAssistant />
        </Suspense>
        </TenantUnitGuard>
      </TenantUnitProvider>
    );
  }

  // Role-based routing for unit owners (role is mapped from 'unit_owner' to 'owner' in useAuth)
  if (user?.role === "owner") {
    return (
      <OwnerUnitProvider>
        <OwnerUnitGuard>
          {/* ESIGN-01 CA-01: same as the renter branch above. */}
          <ElectronicRecordsConsentGuard>
          <Switch>
            <Route path="/owner/unit-select" component={OwnerUnitSelectPage} />
            {/* CONSENT-01 CA-09: la pagina deriva la superficie del rol, no de la URL. */}
            <Route path="/consent/:consentType" component={ConsentPage} />
            <Route path="/" component={OwnerOverview} />
            <Route path="/dashboard" component={OwnerOverview} />
            <Route path="/owner/dashboard" component={OwnerOverview} />
            <Route path="/owner/financial" component={OwnerFinancial} />
            <Route path="/owner/units/:unitId" component={OwnerUnitDetail} />
            <Route path="/owner/units" component={OwnerUnits} />
            <Route path="/owner/announcements" component={OwnerAnnouncements} />
            <Route path="/announcements" component={OwnerAnnouncements} />
            {/* VOTE-06 CA-05 — the owner's assemblies, on the phone-first portal. */}
            {/* VOTE-03 CA-10 / VOTE-05 CA-05 — one assembly on the phone: his
             * ballot while it is open, its result once closed. The endpoint
             * answers 404 when he is not on the roll. */}
            <Route path="/owner/votes/:id" component={OwnerMotion} />
            <Route path="/owner/votes" component={OwnerVotes} />
            <Route path="/owner/work-orders/new" component={OwnerWorkOrderCreate} />
            <Route path="/owner/work-orders" component={OwnerWorkOrders} />
            <Route path="/owner/bookings/new">
              {/* [868kbhrky] Wrapper for the deep link `/owner/bookings/new`.
               * Renders OwnerBookings with `initialView="reservar"` so the
               * "Reservar" section is the default panel. The original form
               * (OwnerBookingCreate) is dead — its URL kept as a real
               * destination so old email links still resolve. */}
              <OwnerBookings initialView="reservar" />
            </Route>
            <Route path="/owner/bookings" component={OwnerBookings} />
            <Route path="/bookings" component={Bookings} />
            {/* FIN-19: owner-portal `/reports` fallback removed — owners have
             * a dedicated `/owner/financial` surface, and the PM Reports
             * section is gated by `reports:read` which owner also has, but
             * the visible UX for owners remains in the owner portal. */}
            <Route path="/documents" component={OwnerDocuments} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/verify-email-change" component={VerifyEmailChange} />
            <Route path="/account" component={Account} />
            <Route path="/settings" component={SettingsRouteSwitch} />
            <Route component={NotFound} />
          </Switch>
          </ElectronicRecordsConsentGuard>
          {/* Same reasoning as the renter branch above. */}
          <Suspense fallback={null}>
            <OwnerIaAssistant />
          </Suspense>
        </OwnerUnitGuard>
      </OwnerUnitProvider>
    );
  }

  // Default admin/property manager routes
  // DISCLAIMER-01 CA-01: board_member has no branch of its own and lands here too.
  return (
    <DisclaimerGuard>
      <Switch>
        <Route path="/disclaimer" component={DisclaimerPage} />
        {/* CONSENT-01 CA-09: la pagina deriva la superficie del rol, no de la URL. */}
        <Route path="/consent/:consentType" component={ConsentPage} />
        <Route path="/" component={Dashboard} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/dashboard/pm"><RequirePM><PmDashboard /></RequirePM></Route>
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/verify-email-change" component={VerifyEmailChange} />
        <Route path="/properties/:propertyId/settings" component={PropertySettings} />
        <Route path="/properties/:id" component={Properties} />
        <Route path="/properties" component={Properties} />
        <Route path="/units/:id" component={Units} />
        <Route path="/units" component={Units} />
        <Route path="/tenants/:id" component={Renters} />
        <Route path="/tenants" component={Renters} />
        {/* 868mjc2a5 — the "work order created" email links here: the board
         * opens with that order's detail (useWorkOrdersPageState). */}
        <Route path="/work-orders/:id" component={WorkOrders} />
        <Route path="/work-orders" component={WorkOrders} />
        <Route path="/vendors" component={Vendors} />
        <Route path="/payments-module/units/:unitId/account-statement" component={AccountStatement} />
        <Route path="/payments-module" component={PaymentsModule} />
        {/* FIN-19 — Reports section. Gated by `reports: read` so it converges
         * with the sidebar predicate (`navigation.config.ts:281`) — PM, BM,
         * owner, and SA all see it; renter does not (no permission). The
         * permission gate also closes a pre-existing security hole: the
         * previous `/reports` mount here had no guard at all. */}
        <Route path="/reports">
          <RequirePermission resource="reports" action="read">
            <ReportsSectionPage />
          </RequirePermission>
        </Route>
        <Route path="/documents" component={Documents} />
        {/* IA-10 CA-02 — read-only gap inbox. NOT `RequireSuperAdmin` any
         * more, and for the reason the endpoint changed: a gap is filed in the
         * organization of the resident who asked, and the super admin's session
         * lives in `alpro-default`, which holds no buildings. Super-admin-only
         * meant the one role allowed in sat where nothing is ever filed while
         * the property manager — whose organization holds every row, and who is
         * the person who closes a gap — was refused outright.
         *
         * `GET /api/ia/gaps` now admits both (`requireGapsReader`,
         * ia.routes.ts), so this guard has to as well: one stricter than the
         * endpoint hides a page the property manager is entitled to.
         *
         * `RequirePM` is `hasPermission('organization', 'update')`, held by
         * exactly those two roles and by nobody else — the same predicate the
         * loader's route and the sidebar already use, so the link and the page
         * cannot drift. What it does NOT decide is WHICH organization's tray
         * loads: that is the header, and the server is the authority on it. */}
        <Route path="/ia/gaps">
          <RequirePM>
            <IaGaps />
          </RequirePM>
        </Route>
        {/* IA-11 — the knowledge-base loader. Two roles publish now, so this is
         * NOT `RequireSuperAdmin` any more: the `/api/ia/kb` mount admits
         * `super_admin` and `property_manager` (`requireKbPublisher`,
         * ia.routes.ts) and a guard stricter than the endpoint hides a page the
         * property manager is entitled to.
         *
         * `RequirePM` is `hasPermission('organization', 'update')`, which is
         * held by exactly those two roles and by nobody else — board_member has
         * `organization: read`, owner and renter have no `organization` resource
         * at all (`shared/types/permissions.ts`), and `getEffectivePermissions`
         * only ever upgrades `users`. Same predicate the sidebar uses to decide
         * whether to surface the entry, so the link and the route cannot drift.
         *
         * What this guard deliberately does NOT decide is where the write lands.
         * The super admin names the destination organization in a header; the
         * property manager cannot name one at all and publishes into their own
         * session's organization (`ia.kb-target.ts`). That asymmetry is enforced
         * on the server and mirrored on the page, not here. */}
        <Route path="/ia/knowledge-base">
          <RequirePM>
            <IaKbIngest />
          </RequirePM>
        </Route>
        <Route path="/owners/:id" component={Owners} />
        <Route path="/owners" component={Owners} />
        <Route path="/announcements" component={Announcements} />
        <Route path="/access-control"><RequirePM><AccessControl /></RequirePM></Route>
        <Route path="/violations" component={Violations} />
        {/* OR-02 (868kmgfck) CA-08 (P5): the board member reads the
         * official records of the association, read-only, for one of
         * their properties. `RequireBoardMember` mirrors the server's
         * `requireRole(['board_member'])` on the board-portal route —
         * the property manager and the super admin get their own
         * screens in OR-04, and until then they see the same 404 as
         * any other unknown route (EX-06). */}
        <Route path="/official-records">
          <RequireBoardMember>
            <OfficialRecordsBoardPage />
          </RequireBoardMember>
        </Route>
        {/* VOTE-06 CA-05 — PM, board member and super admin on the web. No
         * guard: the three roles that land in this branch all read the list,
         * and what each one may see is scoped by the endpoint (CA-03). */}
        <Route path="/votes" component={Votes} />
        {/* VOTE-01 — create and edit a draft. `RequirePM` is PM and SA, the two
         * roles the endpoint lets write a motion; the board member reads the
         * list but does not get the form. */}
        <Route path="/votes/new"><RequirePM><CreateMotion /></RequirePM></Route>
        <Route path="/votes/:motionId/edit"><RequirePM><CreateMotion /></RequirePM></Route>
        {/* VOTE-02 … VOTE-05 — one page per motion: the monitor and the ballot
         * while it is open, closing it, and its results. After /new and /edit
         * so neither is read as an id. No guard: the endpoint answers 404 for a
         * motion out of the reader's scope. */}
        <Route path="/votes/:id" component={MotionDetail} />
        <Route path="/bookings" component={Bookings} />
        <Route path="/budgets" component={Budgets} />
        <Route path="/budget-detail/:id" component={BudgetDetail} />
        <Route path="/coa-configuration"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/coa-configuration/:id"><RequireSuperAdmin mode="notFound"><COAConfigurationEdit /></RequireSuperAdmin></Route>
        {/* [SA-16 / 868jay9qk CA-01, auditoría 14-oct] Legacy COA URLs preserved with notFound gate for EX-03 consistency.
         * The auditoría fresh del 14-oct-2026 found six legacy COA URLs returning 404 on staging:
         * `/admin/coa`, `/admin/coa-configuration`, `/admin/chart-of-accounts`, `/admin/coa-config`,
         * `/admin/coa-configs`, `/admin/coa/configuration`. Only `/coa-configuration` was registered;
         * the rest fell through to the catch-all 404 page even for SA users. Each entry below
         * renders the same `COAConfiguration` component as the canonical URL, so SA usability is
         * identical. For non-SAs the `mode="notFound"` gate returns `<NotFound />` exactly like
         * the canonical route does — staying consistent with EX-06 (do not confirm the surface
         * exists for non-SAs). Adding these registrations closes CA-01 without exposing the
         * surface to PMs. */}
        <Route path="/admin/coa"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/admin/coa-configuration"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/admin/chart-of-accounts"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/admin/coa-config"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/admin/coa-configs"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        <Route path="/admin/coa/configuration"><RequireSuperAdmin mode="notFound"><COAConfiguration /></RequireSuperAdmin></Route>
        {/* [SA-16 / 868jay9qk B2, ronda 4, Ricardo 14-oct-2026]
         * Payment Settings is SA-only. A PM who opens it by URL must see
         * <NotFound />, the same shape the server emits (404 instead of
         * 403). Mirrors the COA decision (round 2 B1) and the API guard
         * (`requireRoleOrNotFound(['super_admin'])` in
         * server/src/modules/admin/admin.routes.ts). */}
        <Route path="/admin/payment-settings">
          <RequireSuperAdmin mode="notFound">
            <PaymentSettings />
          </RequireSuperAdmin>
        </Route>
        {/* 868mny2ch: job schedules, organizations and companies are SA-only
         * (SA-33). Mirrors `requireSuperAdmin` on /api/admin/schedules. */}
        <Route path="/admin/job-schedules"><RequireSuperAdmin><JobSchedulesTrigger /></RequireSuperAdmin></Route>
        <Route path="/admin/configuration"><RequirePM><ConfigurationManager /></RequirePM></Route>
        <Route path="/admin/user-management"><RequirePM><UserManagement /></RequirePM></Route>
        {/* DISCLAIMER-01 — publishing a version of the CAM disclaimer blocks
         * the portal for every user in every organization until they accept
         * it, so this is not a per-tenant power and `RequirePM` would be too
         * wide. `RequireSuperAdmin` mirrors the endpoint's own
         * `requireSuperAdmin` (legal-admin.routes.ts) so the page and the API
         * cannot drift apart about who may open it. */}
        <Route path="/admin/legal-disclaimer">
          <RequireSuperAdmin>
            <LegalDisclaimerAdmin />
          </RequireSuperAdmin>
        </Route>
        {/* SA-SUG-03 / B5 — DLQ failures listing + retry button + the
         * JobSchedulesContent block below. SA-only: the server's
         * /api/admin/dlq routes are gated by requireSuperAdmin, and the
         * React guard mirrors that. A PM role landing here would see an
         * empty listing because the API would 403, not because there are
         * no failures — gating at the route prevents that confusion. */}
        <Route path="/admin/job-failures">
          <RequireSuperAdmin>
            <JobFailuresPage />
          </RequireSuperAdmin>
        </Route>
        {/* [SA-18 868jay9qq CA-10 / EX-06] Lookup management is
            SA-only; non-SAs see NotFound (matching the 404 the server
            now returns from /api/admin/lookup-*). */}
        <Route path="/admin/lookups"><RequireSuperAdmin mode="notFound"><LookupManagement /></RequireSuperAdmin></Route>
        <Route path="/admin/lookup-values"><RequireSuperAdmin mode="notFound"><LookupValueManagement /></RequireSuperAdmin></Route>
        <Route path="/admin/lookup-values/:typeId"><RequireSuperAdmin mode="notFound"><LookupValueManagement /></RequireSuperAdmin></Route>
        <Route path="/admin/amenities" component={AmenitiesConfig} />
        <Route path="/admin/budget-categories"><RequirePM><BudgetCategories /></RequirePM></Route>
        {/* [SA-33 / 868kbjw74 CA-03 + CA-09] Legacy routes redirect to the
            unified Amenities destination. /admin/amenities?tab=catalog is
            the default (Catalog tab). CA-09 keeps old URLs resolving. */}
        <Route path="/settings/amenities"><Redirect to="/admin/amenities" /></Route>
        <Route path="/amenities-config"><Redirect to="/admin/amenities" /></Route>
        <Route path="/amenities-management"><Redirect to="/admin/amenities" /></Route>
        {/* 868mny2ch: SA-only, the same as /admin/job-failures. */}
        <Route path="/admin/organizations/:id"><RequireSuperAdmin><OrganizationDetail /></RequireSuperAdmin></Route>
        <Route path="/admin/organizations"><RequireSuperAdmin><Organizations /></RequireSuperAdmin></Route>
        <Route path="/companies"><RequireSuperAdmin><Companies /></RequireSuperAdmin></Route>
        <Route path="/companies/:id"><RequireSuperAdmin><CompanyDetail /></RequireSuperAdmin></Route>
        <Route path="/account" component={Account} />
        <Route path="/settings" component={SettingsRouteSwitch} />
        <Route component={NotFound} />
      </Switch>
    </DisclaimerGuard>
  );
}

/**
 * Wrapper component that provides PermissionsContext
 * Extracts role and permissions from useAuth and passes them to PermissionsProvider
 */
function AppWithPermissions({ children }: { children: React.ReactNode }) {
  const { role, permissions } = useAuth();
  
  return (
    <PermissionsProvider role={role} permissions={permissions}>
      {children}
    </PermissionsProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system">
        <IntlProvider>
          <ErrorBoundary>
            <TooltipProvider>
              <Toaster />
              {/* 868mbzt38 D3 — single toast per rate-limit activation */}
              <RateLimitNotice />
              <AppWithPermissions>
                <CompanyProvider>
                  <Suspense fallback={<PageSkeleton />}>
                    <Router />
                  </Suspense>
                </CompanyProvider>
              </AppWithPermissions>
            </TooltipProvider>
          </ErrorBoundary>
        </IntlProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;